import { httpClient } from '@/shared/api';
import type { TeacherEquityNationalKpisDto, TeacherEquitySchoolSpreadDto } from './teacherEquityReport.dto';

const PREFIX = '/app/teacher-equity-report';

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Permissive id reader: accepts string or number ids and stringifies numbers so GUIDs and integer ids both work. */
function readId(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() !== '') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readStr(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim() !== '') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function parseSchoolSpreadItem(raw: unknown): TeacherEquitySchoolSpreadDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = readId(o, ['id', 'Id', 'schoolId', 'SchoolId', 'schoolID', 'SchoolID']);
  const schoolName = readStr(o, ['schoolName', 'SchoolName']);
  const educationalCenterNumber =
    readStr(o, [
      'educationalCenterNumber',
      'EducationalCenterNumber',
      'educationalCenterNo',
      'EducationalCenterNo',
    ]) ?? null;
  /** Backend may emit `district`, `districtName`, or only `districtID`; fall back to the id so the row is never dropped on a missing label. */
  const districtFromName = readStr(o, ['districtName', 'DistrictName', 'district', 'District']);
  const districtFromId = readStr(o, ['districtId', 'DistrictId', 'districtID', 'DistrictID']);
  const district = districtFromName ?? (districtFromId ? `District ${districtFromId}` : '—');
  if (!id || !schoolName) return null;
  const budgetId = readId(o, ['budgetId', 'BudgetId']) ?? null;
  const permCount = readNum(o, ['permCount', 'PermCount']) ?? 0;
  const contCount = readNum(o, ['contCount', 'ContCount']) ?? 0;
  const permSPREAD = readNum(o, ['permSPREAD', 'PermSPREAD', 'permSpread', 'PermSpread']) ?? null;
  const contSPREAD = readNum(o, ['contSPREAD', 'ContSPREAD', 'contSpread', 'ContSpread']) ?? null;
  const permSeverityLabel = readStr(o, ['permSeverityLabel', 'PermSeverityLabel']) ?? '';
  const contSeverityLabel = readStr(o, ['contSeverityLabel', 'ContSeverityLabel']) ?? '';
  const permSeverityColor = readStr(o, ['permSeverityColor', 'PermSeverityColor']) ?? '#94a3b8';
  const contSeverityColor = readStr(o, ['contSeverityColor', 'ContSeverityColor']) ?? '#94a3b8';
  const permAvgPeerVariance = readNum(o, ['permAvgPeerVariance', 'PermAvgPeerVariance']) ?? null;
  const contAvgPeerVariance = readNum(o, ['contAvgPeerVariance', 'ContAvgPeerVariance']) ?? null;
  const permWorstComparison = readStr(o, ['permWorstComparison', 'PermWorstComparison']) ?? null;
  const contWorstComparison = readStr(o, ['contWorstComparison', 'ContWorstComparison']) ?? null;

  const permTotalSalaryMedian =
    readNum(o, ['permTotalSalaryMedian', 'PermTotalSalaryMedian', 'permMedianTotalSalary', 'PermMedianTotalSalary']) ??
    null;
  const contTotalSalaryMedian =
    readNum(o, ['contTotalSalaryMedian', 'ContTotalSalaryMedian', 'contMedianTotalSalary', 'ContMedianTotalSalary']) ??
    null;

  const perDistrictMedianTotalSalary =
    readNum(o, [
      'perDistrictMedianTotalSalary',
      'PerDistrictMedianTotalSalary',
      'permDistrictMedian',
      'PermDistrictMedian',
      'permDistrictPeerMedian',
      'PermDistrictPeerMedian',
    ]) ?? null;
  const contDistrictMedianTotalSalary =
    readNum(o, [
      'contDistrictMedianTotalSalary',
      'ContDistrictMedianTotalSalary',
      'contDistrictMedian',
      'ContDistrictMedian',
      'contDistrictPeerMedian',
      'ContDistrictPeerMedian',
    ]) ?? null;
  const perDistrictTotalSalarySpread =
    readNum(o, [
      'perDistrictTotalSalarySpread',
      'PerDistrictTotalSalarySpread',
      'permDistrictSpread',
      'PermDistrictSpread',
      'permDistrictSalarySpread',
      'PermDistrictSalarySpread',
    ]) ?? null;
  const contDistrictTotalSalarySpread =
    readNum(o, [
      'contDistrictTotalSalarySpread',
      'ContDistrictTotalSalarySpread',
      'contDistrictSpread',
      'ContDistrictSpread',
      'contDistrictSalarySpread',
      'ContDistrictSalarySpread',
    ]) ?? null;

  const perSchoolBandMedianTotalSalary =
    readNum(o, [
      'perSchoolBandMedianTotalSalary',
      'PerSchoolBandMedianTotalSalary',
      'permEnrollmentBandMedian',
      'PermEnrollmentBandMedian',
      'permSchoolBandMedian',
      'PermSchoolBandMedian',
    ]) ?? null;
  const contSchoolBandMedianTotalSalary =
    readNum(o, [
      'contSchoolBandMedianTotalSalary',
      'ContSchoolBandMedianTotalSalary',
      'contEnrollmentBandMedian',
      'ContEnrollmentBandMedian',
      'contSchoolBandMedian',
      'ContSchoolBandMedian',
    ]) ?? null;
  const perSchoolBandTotalSalarySpread =
    readNum(o, [
      'perSchoolBandTotalSalarySpread',
      'PerSchoolBandTotalSalarySpread',
      'permEnrollmentSpread',
      'PermEnrollmentSpread',
      'permEnrollmentBandSpread',
      'PermEnrollmentBandSpread',
    ]) ?? null;
  const contSchoolBandTotalSalarySpread =
    readNum(o, [
      'contSchoolBandTotalSalarySpread',
      'ContSchoolBandTotalSalarySpread',
      'contEnrollmentSpread',
      'ContEnrollmentSpread',
      'contEnrollmentBandSpread',
      'ContEnrollmentBandSpread',
    ]) ?? null;

  const perTuitionBandMedianTotalSalary =
    readNum(o, [
      'perTuitionBandMedianTotalSalary',
      'PerTuitionBandMedianTotalSalary',
      'permTuitionBandMedian',
      'PermTuitionBandMedian',
      'permTuitionMedian',
      'PermTuitionMedian',
    ]) ?? null;
  const contTuitionBandMedianTotalSalary =
    readNum(o, [
      'contTuitionBandMedianTotalSalary',
      'ContTuitionBandMedianTotalSalary',
      'contTuitionBandMedian',
      'ContTuitionBandMedian',
      'contTuitionMedian',
      'ContTuitionMedian',
    ]) ?? null;
  const perTuitionBandTotalSalarySpread =
    readNum(o, [
      'perTuitionBandTotalSalarySpread',
      'PerTuitionBandTotalSalarySpread',
      'permTuitionSpread',
      'PermTuitionSpread',
      'permTuitionBandSpread',
      'PermTuitionBandSpread',
    ]) ?? null;
  const contTuitionBandTotalSalarySpread =
    readNum(o, [
      'contTuitionBandTotalSalarySpread',
      'ContTuitionBandTotalSalarySpread',
      'contTuitionSpread',
      'ContTuitionSpread',
      'contTuitionBandSpread',
      'ContTuitionBandSpread',
    ]) ?? null;

  const perParentTuitionBandMedianTotalSalary =
    readNum(o, [
      'perParentTuitionBandMedianTotalSalary',
      'PerParentTuitionBandMedianTotalSalary',
      'permParentTuitionBandMedian',
      'PermParentTuitionBandMedian',
      'permParentTuitionMedian',
      'PermParentTuitionMedian',
    ]) ?? null;
  const contParentTuitionBandMedianTotalSalary =
    readNum(o, [
      'contParentTuitionBandMedianTotalSalary',
      'ContParentTuitionBandMedianTotalSalary',
      'contParentTuitionBandMedian',
      'ContParentTuitionBandMedian',
      'contParentTuitionMedian',
      'ContParentTuitionMedian',
    ]) ?? null;
  const perParentTuitionBandTotalSalarySpread =
    readNum(o, [
      'perParentTuitionBandTotalSalarySpread',
      'PerParentTuitionBandTotalSalarySpread',
      'permParentTuitionSpread',
      'PermParentTuitionSpread',
      'permParentTuitionBandSpread',
      'PermParentTuitionBandSpread',
    ]) ?? null;
  const contParentTuitionBandTotalSalarySpread =
    readNum(o, [
      'contParentTuitionBandTotalSalarySpread',
      'ContParentTuitionBandTotalSalarySpread',
      'contParentTuitionSpread',
      'ContParentTuitionSpread',
      'contParentTuitionBandSpread',
      'ContParentTuitionBandSpread',
    ]) ?? null;

  const permDistrictVariance =
    readNum(o, [
      'permDistrictVariance',
      'PermDistrictVariance',
      'permDistrictVariancePct',
      'PermDistrictVariancePct',
      'permVsDistrictPct',
      'PermVsDistrictPct',
    ]) ?? null;
  const contDistrictVariance =
    readNum(o, [
      'contDistrictVariance',
      'ContDistrictVariance',
      'contDistrictVariancePct',
      'ContDistrictVariancePct',
      'contVsDistrictPct',
      'ContVsDistrictPct',
    ]) ?? null;
  const permSchoolBandVariance =
    readNum(o, [
      'permSchoolBandVariance',
      'PermSchoolBandVariance',
      'permEnrollmentVariancePct',
      'PermEnrollmentVariancePct',
      'permVsEnrollmentPct',
      'PermVsEnrollmentPct',
    ]) ?? null;
  const contSchoolBandVariance =
    readNum(o, [
      'contSchoolBandVariance',
      'ContSchoolBandVariance',
      'contEnrollmentVariancePct',
      'ContEnrollmentVariancePct',
      'contVsEnrollmentPct',
      'ContVsEnrollmentPct',
    ]) ?? null;
  const permTuitionBandVariance =
    readNum(o, [
      'permTuitionBandVariance',
      'PermTuitionBandVariance',
      'permTuitionVariancePct',
      'PermTuitionVariancePct',
      'permVsTuitionPct',
      'PermVsTuitionPct',
    ]) ?? null;
  const contTuitionBandVariance =
    readNum(o, [
      'contTuitionBandVariance',
      'ContTuitionBandVariance',
      'contTuitionVariancePct',
      'ContTuitionVariancePct',
      'contVsTuitionPct',
      'ContVsTuitionPct',
    ]) ?? null;
  const permParentTuitionBandVariance =
    readNum(o, [
      'permParentTuitionBandVariance',
      'PermParentTuitionBandVariance',
      'permParentTuitionVariancePct',
      'PermParentTuitionVariancePct',
      'permVsParentTuitionPct',
      'PermVsParentTuitionPct',
    ]) ?? null;
  const contParentTuitionBandVariance =
    readNum(o, [
      'contParentTuitionBandVariance',
      'ContParentTuitionBandVariance',
      'contParentTuitionVariancePct',
      'ContParentTuitionVariancePct',
      'contVsParentTuitionPct',
      'ContVsParentTuitionPct',
    ]) ?? null;

  return {
    id,
    schoolName,
    budgetId,
    educationalCenterNumber,
    district,
    permCount,
    contCount,
    permSPREAD,
    contSPREAD,
    permSeverityLabel,
    contSeverityLabel,
    permSeverityColor,
    contSeverityColor,
    permAvgPeerVariance,
    contAvgPeerVariance,
    permWorstComparison,
    contWorstComparison,
    permTotalSalaryMedian,
    contTotalSalaryMedian,
    perDistrictMedianTotalSalary,
    contDistrictMedianTotalSalary,
    perDistrictTotalSalarySpread,
    contDistrictTotalSalarySpread,
    perSchoolBandMedianTotalSalary,
    contSchoolBandMedianTotalSalary,
    perSchoolBandTotalSalarySpread,
    contSchoolBandTotalSalarySpread,
    perTuitionBandMedianTotalSalary,
    contTuitionBandMedianTotalSalary,
    perTuitionBandTotalSalarySpread,
    contTuitionBandTotalSalarySpread,
    perParentTuitionBandMedianTotalSalary,
    contParentTuitionBandMedianTotalSalary,
    perParentTuitionBandTotalSalarySpread,
    contParentTuitionBandTotalSalarySpread,
    permDistrictVariance,
    contDistrictVariance,
    permSchoolBandVariance,
    contSchoolBandVariance,
    permTuitionBandVariance,
    contTuitionBandVariance,
    permParentTuitionBandVariance,
    contParentTuitionBandVariance,
  };
}

function readSchoolSpreadArray(
  o: Record<string, unknown>,
  keys: string[]
): { rows: TeacherEquitySchoolSpreadDto[]; rawCount: number; key: string } | undefined {
  for (const k of keys) {
    const v = o[k];
    if (!Array.isArray(v)) continue;
    const rows = v.map(parseSchoolSpreadItem).filter((x): x is TeacherEquitySchoolSpreadDto => x != null);
    return { rows, rawCount: v.length, key: k };
  }
  return undefined;
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

export function mapTeacherEquityNationalKpis(raw: unknown): TeacherEquityNationalKpisDto | null {
  const o = unwrapPayload(raw);
  if (!o) return null;

  const overallMedianPermanent =
    readNum(o, ['overallMedianPermanent', 'OverallMedianPermanent']) ?? null;
  const overallMedianContractual =
    readNum(o, ['overallMedianContractual', 'OverallMedianContractual']) ?? null;
  const overallAveragePermanent =
    readNum(o, ['overallAveragePermanent', 'OverallAveragePermanent']) ?? null;
  const overallAverageContractual =
    readNum(o, ['overallAverageContractual', 'OverallAverageContractual']) ?? null;
  const avgInternalSpreadPermanent =
    readNum(o, ['avgInternalSpreadPermanent', 'AvgInternalSpreadPermanent']) ?? null;
  const avgInternalSpreadContractual =
    readNum(o, ['avgInternalSpreadContractual', 'AvgInternalSpreadContractual']) ?? null;

  const schoolSpreads = readSchoolSpreadArray(o, ['schoolSpreads', 'SchoolSpreads']);

  if (import.meta.env.DEV) {
    const missingMedians = overallMedianPermanent == null && overallMedianContractual == null;
    if (missingMedians) {
      // eslint-disable-next-line no-console
      console.info('[teacher-equity] national medians absent (often insufficient sample for filters); still mapping schoolSpreads', {
        topLevelKeys: Object.keys(o),
        schoolSpreadCount: schoolSpreads?.rows.length ?? 0,
      });
    }
    // eslint-disable-next-line no-console
    console.debug('[teacher-equity] parsed national KPIs payload', {
      topLevelKeys: Object.keys(o),
      schoolSpreads: schoolSpreads
        ? { matchedKey: schoolSpreads.key, raw: schoolSpreads.rawCount, kept: schoolSpreads.rows.length }
        : 'missing',
    });
  }

  return {
    overallMedianPermanent,
    overallMedianContractual,
    overallAveragePermanent,
    overallAverageContractual,
    avgInternalSpreadPermanent,
    avgInternalSpreadContractual,
    schoolSpreads: schoolSpreads?.rows ?? [],
  };
}

export type TeacherEquityReportQueryParams = {
  year: number;
  /**
   * Role filter — repeated `RoleIds` query keys. **Empty array = all roles** (params omitted; matches typical ABP
   * “no filter” behavior until backend documents otherwise).
   */
  roleIds?: string[];
  /**
   * Qualification filter — repeated `QualificationsIds` query keys (Swagger naming). **Empty = all qualifications.**
   */
  qualificationIds?: string[];
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
};

function appendIdArray(q: URLSearchParams, paramName: string, ids: string[] | undefined): void {
  const trimmed = (ids ?? []).map((x) => x.trim()).filter(Boolean);
  for (const id of trimmed) {
    q.append(paramName, id);
  }
}

export const teacherEquityReportApi = {
  getNationalKpis: async (params: TeacherEquityReportQueryParams): Promise<TeacherEquityNationalKpisDto> => {
    const q = new URLSearchParams();
    q.set('Year', String(params.year));
    appendIdArray(q, 'RoleIds', params.roleIds);
    appendIdArray(q, 'QualificationsIds', params.qualificationIds);
    if (params.sorting != null && params.sorting !== '') q.set('Sorting', params.sorting);
    if (params.skipCount != null) q.set('SkipCount', String(params.skipCount));
    if (params.maxResultCount != null) q.set('MaxResultCount', String(params.maxResultCount));
    const raw = await httpClient.get<unknown>(`${PREFIX}?${q.toString()}`);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[teacher-equity] raw response', raw);
    }
    const mapped = mapTeacherEquityNationalKpis(raw);
    if (!mapped) {
      throw new Error('Teacher equity report response could not be read (empty or invalid payload).');
    }
    return mapped;
  },
};
